<script lang="ts">
  import { page } from '$app/stores';
  import type { Race } from '$types/races';
  import { isSubscribed } from '$utils/auth';
  import { selectedRaceId } from '$utils/navigation';
  import { sidebarOpen } from '$utils/ui';
  import * as Tooltip from '$lib/components/ui/tooltip';
  import TablerFilterSearch from '~icons/tabler/filter-search';
  import ApplicationLogoIcon from './ApplicationLogoIcon.svelte';
  import QueryFilterInput from './QueryFilterInput.svelte';
  import SidebarMenuDropdown from './SidebarMenuDropdown.svelte';
  import UserInfoDropdown from './UserInfoDropdown.svelte';
  import NextToJumpButton from './race/NextToJumpButton.svelte';
  import RaceFilters from './race/RaceFilters.svelte';
  import RaceList from './race/RaceList.svelte';
  import RainbowButton from './RainbowButton.svelte';
  import { calculateTimeToJump, currentTime } from '$utils/time';

  $: ({ races, featureFlags } = $page.data);
  $: hasAdvancedSearch = $featureFlags?.includes('advanced_search');

  export let filteredRaces: Race[];

  let scrollToRace: (raceId?: string) => Promise<void>;

  const specialRaceStartTime = new Date('2024-11-05T04:00:00Z');
  let specialRaceTTJ: string | null = null;
  $: {
    const now = $currentTime;
    if (specialRaceStartTime.getTime() + 60 * 60 * 1000 < now.getTime()) {
      specialRaceTTJ = null;
    } else {
      specialRaceTTJ = calculateTimeToJump(specialRaceStartTime, now);
    }
  }
</script>

<div
  class="inset-y-0 z-[49] flex w-72 border-r border-gray-200 bg-white lg:flex
    {$sidebarOpen ? 'fixed' : 'hidden lg:fixed '}"
>
  <div class="flex min-h-0 flex-1 flex-col">
    <div class="ml-2 flex" id="betwatch-sidebar-logo">
      <!-- BEGIN LOGO HEADER -->
      <div class="ml-2 flex pb-2 pt-2">
        <div class="px-1">
          <ApplicationLogoIcon />
        </div>
        <span
          class="ml-1 flex flex-1 select-none items-center bg-gradient-to-tr from-fuchsia-800 to-black bg-clip-text text-[30px] font-black text-transparent subpixel-antialiased"
          >BETWATCH</span
        >
      </div>
    </div>

    <!-- END LOGO HEADER -->
    {#if $isSubscribed}
      <div class="flex flex-row">
        <QueryFilterInput
          on:filterChange={() => {
            $selectedRaceId && scrollToRace($selectedRaceId);
          }}
        />
        {#if hasAdvancedSearch}
          <Tooltip.Root>
            <Tooltip.Trigger>
              <a
                on:click={() => {
                  sidebarOpen.set(false);
                  selectedRaceId.set(null);
                }}
                href="/app/racing/search"
                class="relative mr-2 flex h-full cursor-pointer items-center justify-center rounded-lg hover:bg-gray-100"
              >
                <TablerFilterSearch class="w-8" />
              </a>
            </Tooltip.Trigger>
            <Tooltip.Content side="bottom">
              <p>Advanced Search</p>
            </Tooltip.Content>
          </Tooltip.Root>
        {/if}

        <SidebarMenuDropdown />
      </div>
    {/if}
    <!-- BEGIN FILTER BUTTONS -->

    <!-- END FILTER BUTTONS -->

    {#if $races?.data?.races.length}
      <NextToJumpButton races={filteredRaces} />
      <!-- {#if specialRaceTTJ}
        <a
          href="/app/racing/2024-11-05/T/Flemington/7"
          data-sveltekit-reload
          class="flex text-xs"
        >
          <RainbowButton class="mx-2 mb-2 h-8 w-full">
            <div class="flex w-full flex-row text-sm">
              <span class="flex flex-1">Melbourne Cup</span>
              <span
                class="max-w-md justify-end pl-2 font-light text-indigo-200 transition duration-200 group-hover:text-gray-100"
                >{specialRaceTTJ}</span
              >
            </div>
          </RainbowButton>
        </a>
      {/if} -->
      <RaceFilters />
    {/if}

    {#if $races?.data?.races.length}
      <RaceList races={filteredRaces} bind:scrollToRace />
    {/if}

    <UserInfoDropdown />
  </div>
</div>

<div
  role="button"
  tabindex="0"
  class="top-0 z-40 h-full w-full bg-black bg-opacity-50 transition-all duration-300
  {$sidebarOpen ? 'fixed lg:hidden' : 'hidden'}"
  on:click={() => sidebarOpen.set(false)}
  on:keydown={() => sidebarOpen.set(false)}
/>
